import { getEffectiveNow, isOpenEverything } from '../../../utils/systemTime';
import { useState, useEffect, useCallback, useRef } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';
import StudentInstructorFeedback from './StudentInstructorFeedback';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const AUTO_REFRESH_MS = 30000;

function readStoredWeekId(key) {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&pd_enabled=eq.true&select=*&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchSubmissions(enrolmentId) {
  // Fetch submissions first, without relying on an embedded feedback relation.
  const submissionResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?enrolment_id=eq.${enrolmentId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  if (!submissionResponse.ok) {
    throw new Error(`Failed to load philosophical diary submissions (${submissionResponse.status})`);
  }

  const rows = await submissionResponse.json();
  if (rows.length === 0) return [];

  const submissionIds = rows.map(row => row.id).join(',');

  // Students should only receive feedback that the instructor has actually released.
  const feedbackResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_feedback?pd_submission_id=in.(${submissionIds})&submitted=eq.true&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  if (!feedbackResponse.ok) {
    throw new Error(`Failed to load instructor feedback (${feedbackResponse.status})`);
  }

  const feedbackRows = await feedbackResponse.json();

  return rows.map(row => {
    const candidates = feedbackRows.filter(
      feedback => feedback.pd_submission_id === row.id
    );

    // If historical duplicate rows exist, prefer the most recently submitted one.
    candidates.sort((a, b) => {
      const aTime = new Date(a.submitted_at || a.created_at || 0).getTime();
      const bTime = new Date(b.submitted_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });

    return {
      ...row,
      pd_feedback: candidates[0] || null
    };
  });
}


async function fetchPdAiDisclosureContext(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [settingsResponse, permissionsResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=ai_disclosure_enabled,assignment_type_id,assignment_types(name)`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_ai_permissions?course_id=eq.${courseId}&permitted=eq.true&select=id,note,ai_use_types(label,description,ai_use_categories(name,display_order))`,
      { headers }
    )
  ]);

  if (!settingsResponse.ok) {
    throw new Error(
      `Failed to load AI Disclosure setting (${settingsResponse.status})`
    );
  }

  if (!permissionsResponse.ok) {
    throw new Error(
      `Failed to load permitted AI uses (${permissionsResponse.status})`
    );
  }

  const settings = await settingsResponse.json();
  const permissions = await permissionsResponse.json();

  const pdSetting = (Array.isArray(settings) ? settings : []).find(
    row => row.assignment_types?.name === 'Philosophical Diary'
  );

  const permittedAiUses = (Array.isArray(permissions) ? permissions : [])
    .filter(row => row.permitted !== false)
    .sort((a, b) => {
      const aOrder =
        Number(a.ai_use_types?.ai_use_categories?.display_order) || 0;
      const bOrder =
        Number(b.ai_use_types?.ai_use_categories?.display_order) || 0;

      if (aOrder !== bOrder) return aOrder - bOrder;

      return String(a.ai_use_types?.label || '').localeCompare(
        String(b.ai_use_types?.label || '')
      );
    });

  return {
    enabled: !!pdSetting?.ai_disclosure_enabled,
    permittedAiUses
  };
}

async function submitPd(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updatePd(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function submitFeedbackResponse(feedbackId, responseHtml) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_feedback?id=eq.${feedbackId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({ student_response: responseHtml })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to submit response (${response.status})`);
  }

  const result = await response.json();
  if (!result?.[0]) {
    throw new Error('Instructor feedback record was not found or could not be updated.');
  }

  return result[0];
}

function hasMeaningfulRichText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length > 0;
}

function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const match = String(dateStr).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0, 0, 0, 0
  );
}

function getPdDeadline(week, course, submission) {
  if (!week?.class_date || !course?.pd_due_day) return null;

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const targetDay = dayNames.indexOf(course.pd_due_day);
  if (targetDay < 0) return null;

  const dueDate = parseLocalDate(week.class_date);
  if (!dueDate) return null;

  while (dueDate.getDay() !== targetDay) {
    dueDate.setDate(dueDate.getDate() + 1);
  }

  const extensionDays = Math.max(0, Number(submission?.extension_days) || 0);
  dueDate.setDate(dueDate.getDate() + extensionDays);
  dueDate.setHours(23, 59, 59, 999);

  return dueDate;
}

function getWindowStatus(week, course, session, submission = null) {
  if (isOpenEverything(session)) return 'open';

  const classDate = parseLocalDate(week?.class_date);
  if (!classDate) return 'closed';

  const now = getEffectiveNow(session);
  if (now < classDate) return 'not_open';

  const dueDate = getPdDeadline(week, course, submission);
  if (!dueDate) return 'closed';

  return now <= dueDate ? 'open' : 'closed';
}

function formatDeadline(date) {
  if (!date) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short'
  });
}


function isValidHttpsUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function AiPolicyInfo({ permittedAiUses }) {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        In this course, AI can be used in only <u>the following ways</u>:
      </div>

      {permittedAiUses.length === 0 ? (
        <div style={styles.noAiAllowed}>
          This course does not currently permit any uses of AI.
        </div>
      ) : (
        <div style={styles.permissionList}>
          {permittedAiUses.map(permission => (
            <div key={permission.id} style={styles.permissionItem}>
              <div style={styles.permissionTitle}>
                {permission.ai_use_types?.label || 'Permitted AI use'}
              </div>
              {permission.ai_use_types?.description && (
                <div style={styles.permissionDescription}>
                  {permission.ai_use_types.description}
                </div>
              )}
              {permission.note && (
                <div style={styles.permissionNote}>
                  Course note: {permission.note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SharingUrlInfo() {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        The Sharing URL is not the same as the URL of your conversation.
      </div>
      <div style={styles.infoText}>
        Use the conversation's Share control, create a sharing link, then
        paste that sharing URL here.
      </div>

      <div style={styles.providerGuideList}>
        {[
          ['ChatGPT', 'Open conversation', 'Share', 'Create / copy link'],
          ['Claude', 'Open conversation', 'Share', 'Create / copy link'],
          ['Gemini', 'Open conversation', 'Share', 'Create / copy public link']
        ].map(([provider, step1, step2, step3]) => (
          <div key={provider} style={styles.providerGuide}>
            <div style={styles.providerName}>{provider}</div>
            <div style={styles.providerMockWindow}>
              <span>{step1}</span>
              <span style={styles.providerArrow}>→</span>
              <span style={styles.providerShareButton}>{step2}</span>
              <span style={styles.providerArrow}>→</span>
              <span>{step3}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.infoSmall}>
        Share only the conversation relevant to this submission. Do not
        include unrelated private or personal information.
      </div>
    </div>
  );
}

function AiDisclosurePanel({
  aiUsed,
  setAiUsed,
  policyConfirmed,
  setPolicyConfirmed,
  conversationUrl,
  setConversationUrl,
  permittedAiUses
}) {
  const [showPolicyInfo, setShowPolicyInfo] = useState(false);
  const [showSharingInfo, setShowSharingInfo] = useState(false);

  function chooseAiUsed(nextValue) {
    setAiUsed(nextValue);

    if (!nextValue) {
      setPolicyConfirmed(false);
      setConversationUrl('');
      setShowPolicyInfo(false);
      setShowSharingInfo(false);
    }
  }

  return (
    <div style={styles.aiDisclosurePanel}>
      <div style={styles.aiDisclosureHeading}>AI Disclosure</div>

      <div style={styles.aiQuestion}>
        Did you use AI in creating this submission?
      </div>

      <div style={styles.radioRow}>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name="pd-ai-used"
            checked={aiUsed === false}
            onChange={() => chooseAiUsed(false)}
          />
          No
        </label>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name="pd-ai-used"
            checked={aiUsed === true}
            onChange={() => chooseAiUsed(true)}
          />
          Yes
        </label>
      </div>

      {aiUsed && (
        <div style={styles.aiYesArea}>
          {permittedAiUses.length === 0 && (
            <div style={styles.noAiAllowed}>
              This course does not permit any uses of AI for this
              assignment. You cannot submit with “Yes” selected.
            </div>
          )}

          <div style={styles.confirmationRow}>
            <span>
              I confirm that I used AI for this assignment in accordance
              with this course's AI Policy.
            </span>
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowPolicyInfo(prev => !prev)}
              aria-label="Show permitted AI uses"
              title="Show permitted AI uses"
            >
              ?
            </button>
            <input
              type="checkbox"
              checked={policyConfirmed}
              onChange={event =>
                setPolicyConfirmed(event.target.checked)
              }
            />
          </div>

          {showPolicyInfo && (
            <AiPolicyInfo permittedAiUses={permittedAiUses} />
          )}

          <div style={styles.urlRow}>
            <input
              type="url"
              style={styles.urlInput}
              value={conversationUrl}
              onChange={event =>
                setConversationUrl(event.target.value)
              }
              placeholder="Enter AI Conversation Sharing URL here"
            />
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowSharingInfo(prev => !prev)}
              aria-label="How to find a sharing URL"
              title="How to find a sharing URL"
            >
              ?
            </button>
          </div>

          {showSharingInfo && <SharingUrlInfo />}
        </div>
      )}
    </div>
  );
}

function AiDisclosureReadOnly({ submission }) {
  if (typeof submission?.ai_used !== 'boolean') return null;

  return (
    <div style={styles.aiDisclosureReadOnly}>
      <div style={styles.feedbackLabel}>AI Disclosure</div>
      <div>
        Used AI: <strong>{submission.ai_used ? 'Yes' : 'No'}</strong>
      </div>
      {submission.ai_used && submission.ai_conversation_url && (
        <div style={styles.aiDisclosureLinkRow}>
          Conversation sharing URL:{' '}
          <a
            href={submission.ai_conversation_url}
            target="_blank"
            rel="noreferrer"
          >
            {submission.ai_conversation_url}
          </a>
        </div>
      )}
    </div>
  );
}

export default function StudentPd({ session, course, enrolment }) {
  const storageKey = `student-system:pd:${course.id}:${enrolment.id}:week`;

  const [weeks, setWeeks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeekId, setSelectedWeekId] = useState(() =>
    readStoredWeekId(storageKey)
  );
  const [text, setText] = useState('');
  const [feedbackResponse, setFeedbackResponse] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [aiDisclosureEnabled, setAiDisclosureEnabled] = useState(false);
  const [permittedAiUses, setPermittedAiUses] = useState([]);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiPolicyConfirmed, setAiPolicyConfirmed] = useState(false);
  const [aiConversationUrl, setAiConversationUrl] = useState('');

  const refreshInFlight = useRef(false);
  const editorInitialisedForWeek = useRef(null);

  const refreshData = useCallback(async ({ showLoading = false } = {}) => {
    if (refreshInFlight.current) return null;

    refreshInFlight.current = true;
    if (showLoading) setLoading(true);

    try {
      const [freshWeeks, freshSubmissions] = await Promise.all([
        fetchWeeks(course.id),
        fetchSubmissions(enrolment.id)
      ]);

      setWeeks(freshWeeks);
      setSubmissions(freshSubmissions);

      return {
        weeks: freshWeeks,
        submissions: freshSubmissions
      };
    } catch (err) {
      console.error('Failed to refresh philosophical diary:', err);
      setError('Failed to refresh philosophical diary.');
      return null;
    } finally {
      refreshInFlight.current = false;
      setLoading(false);
    }
  }, [course.id, enrolment.id]);

  useEffect(() => {
    refreshData({ showLoading: true });
  }, [refreshData]);

  useEffect(() => {
    let cancelled = false;

    fetchPdAiDisclosureContext(course.id)
      .then(context => {
        if (cancelled) return;
        setAiDisclosureEnabled(context.enabled);
        setPermittedAiUses(context.permittedAiUses);
      })
      .catch(err => {
        console.error('Failed to load PD AI Disclosure context:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const intervalId = window.setInterval(() => {
      refreshData();
    }, AUTO_REFRESH_MS);

    const handleFocus = () => refreshData();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (selectedWeekId) {
        window.sessionStorage.setItem(storageKey, selectedWeekId);
      } else {
        window.sessionStorage.removeItem(storageKey);
      }
    } catch {
      // Navigation persistence is optional.
    }
  }, [storageKey, selectedWeekId]);

  const loadDisclosureFromSubmission = useCallback((submission) => {
    if (typeof submission?.ai_used === 'boolean') {
      setAiUsed(submission.ai_used);
      setAiPolicyConfirmed(!!submission.ai_policy_confirmed);
      setAiConversationUrl(submission.ai_conversation_url || '');
    } else {
      setAiUsed(false);
      setAiPolicyConfirmed(false);
      setAiConversationUrl('');
    }
  }, []);

  const selectedWeek = weeks.find(week => week.id === selectedWeekId) || null;

  function getSubmission(weekId, source = submissions) {
    return source.find(submission => submission.week_id === weekId);
  }

  // Restore editor content only when entering/restoring a week.
  // Timed refreshes must not overwrite unsaved text.
  useEffect(() => {
    if (!selectedWeekId || !selectedWeek) {
      if (!selectedWeekId) {
        editorInitialisedForWeek.current = null;
      }
      return;
    }

    if (editorInitialisedForWeek.current === selectedWeekId) {
      return;
    }

    const existing = submissions.find(
      submission => submission.week_id === selectedWeekId
    );
    setText(existing?.text || '');
    setFeedbackResponse(existing?.pd_feedback?.student_response || '');
    loadDisclosureFromSubmission(existing);
    editorInitialisedForWeek.current = selectedWeekId;
  }, [
    selectedWeekId,
    selectedWeek,
    submissions,
    loadDisclosureFromSubmission
  ]);

  async function handleSelectWeek(week) {
    setError('');
    setSuccess('');

    const refreshed = await refreshData();
    const freshSubmissions = refreshed?.submissions || submissions;
    const existing = getSubmission(week.id, freshSubmissions);

    setSelectedWeekId(week.id);
    setText(existing?.text || '');
    setFeedbackResponse(existing?.pd_feedback?.student_response || '');
    loadDisclosureFromSubmission(existing);
    editorInitialisedForWeek.current = week.id;
  }

  async function handleBackToWeeks() {
    setSelectedWeekId(null);
    editorInitialisedForWeek.current = null;
    setText('');
    setFeedbackResponse('');
    setAiUsed(false);
    setAiPolicyConfirmed(false);
    setAiConversationUrl('');
    setError('');
    setSuccess('');
    await refreshData();
  }

  async function handleSaveDraft(e) {
    e.preventDefault();
    setError('');

    const existing = getSubmission(selectedWeek.id);

    if (existing?.submitted) {
      return setError('This entry has already been submitted and can no longer be edited.');
    }

    if (!hasMeaningfulRichText(text)) {
      return setError('Please write something before saving.');
    }

    const status = getWindowStatus(selectedWeek, course, session, existing);
    if (status !== 'open') {
      return setError('The submission window is not currently open.');
    }

    setSaving(true);

    try {
      if (existing) {
        const result = await updatePd(existing.id, { text: text.trim() });
        setSubmissions(prev =>
          prev.map(s => s.id === existing.id ? { ...s, ...result[0] } : s)
        );
      } else {
        const result = await submitPd({
          enrolment_id: enrolment.id,
          week_id: selectedWeek.id,
          text: text.trim(),
          submitted: false
        });

        setSubmissions(prev => [...prev, result[0]]);
      }

      setSuccess('Draft saved.');
      await refreshData();
    } catch (err) {
      console.error('PD draft save failed:', err);
      setError('Failed to save draft.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    const existing = getSubmission(selectedWeek.id);

    if (existing?.submitted) {
      return setError('This entry has already been submitted and can no longer be edited.');
    }

    if (!hasMeaningfulRichText(text)) {
      return setError('Please write something before submitting.');
    }

    const status = getWindowStatus(selectedWeek, course, session, existing);
    if (status !== 'open') {
      return setError('The submission window is not currently open.');
    }

    if (aiDisclosureEnabled && aiUsed) {
      if (permittedAiUses.length === 0) {
        return setError(
          'This course does not permit any uses of AI for this assignment.'
        );
      }

      if (!aiPolicyConfirmed) {
        return setError(
          'You must confirm that your AI use complied with the course AI Policy.'
        );
      }

      if (!isValidHttpsUrl(aiConversationUrl)) {
        return setError(
          'Enter a valid HTTPS AI conversation sharing URL.'
        );
      }
    }

    const disclosureData = aiDisclosureEnabled
      ? {
          ai_used: !!aiUsed,
          ai_policy_confirmed: aiUsed
            ? !!aiPolicyConfirmed
            : false,
          ai_conversation_url: aiUsed
            ? aiConversationUrl.trim()
            : null
        }
      : {};

    setSaving(true);

    try {
      let submittedRow;

      if (existing) {
        const result = await updatePd(existing.id, {
          text: text.trim(),
          submitted: true,
          submitted_at: new Date().toISOString(),
          ...disclosureData
        });
        submittedRow = result[0];

        setSubmissions(prev =>
          prev.map(s =>
            s.id === existing.id
              ? { ...s, ...submittedRow, pd_feedback: s.pd_feedback || null }
              : s
          )
        );
      } else {
        const result = await submitPd({
          enrolment_id: enrolment.id,
          week_id: selectedWeek.id,
          text: text.trim(),
          submitted: true,
          submitted_at: new Date().toISOString(),
          ...disclosureData
        });

        submittedRow = { ...result[0], pd_feedback: null };
        setSubmissions(prev => [...prev, submittedRow]);
      }

      setText(submittedRow?.text || text);
      setSuccess('');
      await refreshData();
    } catch (err) {
      console.error('PD submission failed:', err);
      setError('Failed to submit.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFeedbackResponse(e) {
    e.preventDefault();
    setError('');

    const existing = getSubmission(selectedWeek.id);
    const feedback = existing?.pd_feedback;

    if (!existing?.submitted || !feedback?.submitted) {
      return setError('Instructor feedback is not available for response.');
    }

    if (feedback.student_response) {
      return setError('Your response has already been submitted and can no longer be edited.');
    }

    if (!hasMeaningfulRichText(feedbackResponse)) {
      return setError('Please write a response.');
    }

    setSaving(true);

    try {
      const updatedFeedback = await submitFeedbackResponse(
        feedback.id,
        feedbackResponse.trim()
      );

      setSubmissions(prev =>
        prev.map(s =>
          s.id === existing.id
            ? { ...s, pd_feedback: { ...s.pd_feedback, ...updatedFeedback } }
            : s
        )
      );

      setFeedbackResponse(updatedFeedback.student_response || feedbackResponse.trim());
      setSuccess('Response submitted. Your response is now locked.');
      await refreshData();
    } catch (err) {
      console.error('PD feedback response failed:', err);
      setError(err?.message || 'Failed to submit response.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>Loading...</div>
      </div>
    );
  }

  if (selectedWeek) {
    const existing = getSubmission(selectedWeek.id);
    const status = getWindowStatus(selectedWeek, course, session, existing);
    const isOpen = status === 'open';
    const isSubmitted = !!existing?.submitted;
    const feedback = existing?.pd_feedback || null;
    const hasFeedback = !!feedback?.submitted;
    const hasStudentResponse = !!feedback?.student_response;

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button
            style={styles.backLink}
            onClick={handleBackToWeeks}
          >
            ← Back to weeks
          </button>

          <h2 style={styles.heading}>
            Week {selectedWeek.week_number} — {formatDate(selectedWeek.class_date)}
          </h2>

          {Number(existing?.extension_days || 0) > 0 && (
            <div style={styles.extensionNotice}>
              Extension: {existing.extension_days} day{Number(existing.extension_days) === 1 ? '' : 's'}.
              {' '}Extended deadline: {formatDeadline(getPdDeadline(selectedWeek, course, existing))}
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          {/* Before submission: editable rich-text entry */}
          {!isSubmitted && (
            <div style={styles.form}>
              <label style={styles.label}>Your philosophical diary entry</label>

              {isOpen ? (
                <>
                  <RichTextEditor
                    value={text}
                    onChange={setText}
                  />

                  {aiDisclosureEnabled && (
                    <AiDisclosurePanel
                      aiUsed={aiUsed}
                      setAiUsed={setAiUsed}
                      policyConfirmed={aiPolicyConfirmed}
                      setPolicyConfirmed={setAiPolicyConfirmed}
                      conversationUrl={aiConversationUrl}
                      setConversationUrl={setAiConversationUrl}
                      permittedAiUses={permittedAiUses}
                    />
                  )}

                  <div style={styles.btnRow}>
                    <button
                      type="button"
                      style={styles.secondaryBtn}
                      disabled={saving}
                      onClick={handleSaveDraft}
                    >
                      {saving ? 'Saving...' : 'Save Draft'}
                    </button>

                    <button
                      type="button"
                      style={styles.submitBtn}
                      disabled={saving}
                      onClick={handleSubmit}
                    >
                      {saving ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {existing?.text && (
                    <div
                      style={styles.feedbackText}
                      dangerouslySetInnerHTML={{ __html: existing.text }}
                    />
                  )}

                  {status === 'not_open' && (
                    <div style={styles.closedBanner}>
                      The submission window has not opened yet.
                    </div>
                  )}

                  {status === 'closed' && (
                    <div style={styles.closedBanner}>
                      The submission window has closed.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* After submission: entry is always read-only */}
          {isSubmitted && (
            <div style={styles.submittedBox}>
              <div style={styles.feedbackLabel}>Your submitted entry</div>
              <div
                style={styles.feedbackText}
                dangerouslySetInnerHTML={{ __html: existing.text || '' }}
              />
              <div style={styles.lockedNote}>
                Submitted entries cannot be edited.
              </div>
            </div>
          )}

          {isSubmitted && (
            <AiDisclosureReadOnly submission={existing} />
          )}

          {/* Make the feedback area explicit even before feedback is released */}
          {isSubmitted && !hasFeedback && (
            <div style={styles.feedbackPendingBox}>
              <div style={styles.feedbackLabel}>Instructor feedback</div>
              <div style={styles.muted}>No feedback has been released yet.</div>
            </div>
          )}

          {/* Instructor feedback appears below the submitted entry */}
          {isSubmitted && hasFeedback && (
            <div style={styles.feedbackBox}>
              <StudentInstructorFeedback
                submissionType="pd"
                submissionId={
                  existing?.id
                }
                effectiveFeedback={
                  feedback
                }
                gradingMode={
                  course?.grading_mode
                }
              />
            </div>
          )}

          {/* Student can respond once after feedback has been released */}
          {isSubmitted && hasFeedback && !hasStudentResponse && (
            <form onSubmit={handleFeedbackResponse} style={styles.responseForm}>
              <label style={styles.label}>
                Your response to instructor feedback (optional)
              </label>

              <RichTextEditor
                value={feedbackResponse}
                onChange={setFeedbackResponse}
              />

              <button
                type="submit"
                style={styles.secondaryBtn}
                disabled={saving}
              >
                {saving ? 'Submitting...' : 'Submit Response'}
              </button>
            </form>
          )}

          {/* Submitted response is read-only */}
          {isSubmitted && hasFeedback && hasStudentResponse && (
            <div style={styles.responseBox}>
              <div style={styles.feedbackLabel}>Your response</div>
              <div
                style={styles.feedbackText}
                dangerouslySetInnerHTML={{ __html: feedback.student_response }}
              />
              <div style={styles.lockedNote}>
                Submitted responses cannot be edited.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const submittedCount = submissions.filter(s => s.submitted).length;

  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>Philosophical Diary</h2>
        <p style={styles.summary}>
          {submittedCount} of {weeks.length} entries submitted
        </p>
        <div style={styles.refreshNote}>
          Updates automatically every 30 seconds and when you return to this browser tab.
        </div>

        <div style={styles.weekList}>
          {weeks.map(week => {
            const submission = getSubmission(week.id);
            const status = getWindowStatus(week, course, session, submission);
            const isSubmitted = submission?.submitted;
            const hasFeedback = submission?.pd_feedback?.submitted;
            const hasDraft = submission && !isSubmitted;

            return (
              <button
                key={week.id}
                style={styles.weekRow}
                onClick={() => handleSelectWeek(week)}
              >
                <div style={styles.weekLeft}>
                  <div style={styles.weekNum}>Week {week.week_number}</div>
                  <div style={styles.weekDate}>{formatDate(week.class_date)}</div>
                </div>

                <div style={styles.weekRight}>
                  {hasFeedback ? (
                    <span style={styles.feedbackPill}>Feedback received</span>
                  ) : isSubmitted ? (
                    <span style={styles.submittedPill}>✓ Submitted</span>
                  ) : hasDraft ? (
                    <span style={styles.draftPill}>Draft saved</span>
                  ) : status === 'open' ? (
                    <span style={styles.openPill}>Open</span>
                  ) : status === 'not_open' ? (
                    <span style={styles.notOpenPill}>Not yet open</span>
                  ) : (
                    <span style={styles.closedPill}>Closed</span>
                  )}
                </div>

                <span style={styles.arrow}>›</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  aiDisclosurePanel: {
    marginTop: '18px',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '14px',
    background: '#f8fafb'
  },
  aiDisclosureHeading: {
    fontWeight: '800',
    fontSize: '15px',
    color: '#243b53',
    marginBottom: '10px'
  },
  aiQuestion: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#243b53',
    marginBottom: '8px'
  },
  radioRow: {
    display: 'flex',
    gap: '18px',
    marginBottom: '8px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#243b53',
    cursor: 'pointer'
  },
  aiYesArea: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #d9e2ec'
  },
  confirmationRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    gap: '8px',
    alignItems: 'start',
    fontSize: '13px',
    lineHeight: '1.45',
    color: '#243b53'
  },
  infoBtn: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: '1px solid #9fb5c4',
    background: 'white',
    color: '#2f6f9f',
    fontWeight: '800',
    cursor: 'pointer',
    padding: 0,
    lineHeight: '20px'
  },
  infoBox: {
    marginTop: '10px',
    border: '1px solid #c7d6e0',
    borderRadius: '5px',
    padding: '12px',
    background: 'white'
  },
  infoBoxTitle: {
    fontWeight: '700',
    fontSize: '13px',
    color: '#243b53',
    marginBottom: '8px',
    lineHeight: '1.45'
  },
  infoText: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.45',
    marginBottom: '10px'
  },
  infoSmall: {
    fontSize: '11px',
    color: '#829ab1',
    lineHeight: '1.4',
    marginTop: '10px'
  },
  noAiAllowed: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '4px',
    padding: '9px 10px',
    color: '#a12a2a',
    fontSize: '12px',
    lineHeight: '1.4',
    marginBottom: '10px'
  },
  permissionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  permissionItem: {
    borderTop: '1px solid #eef2f5',
    paddingTop: '8px'
  },
  permissionTitle: {
    fontWeight: '800',
    fontSize: '13px',
    color: '#2f6f9f'
  },
  permissionDescription: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.45',
    marginTop: '3px'
  },
  permissionNote: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.4',
    marginTop: '4px',
    fontStyle: 'italic'
  },
  urlRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '8px',
    alignItems: 'center',
    marginTop: '12px'
  },
  urlInput: {
    width: '100%',
    border: '1px solid #bcccdc',
    borderRadius: '4px',
    padding: '9px 10px',
    boxSizing: 'border-box',
    fontSize: '13px',
    background: 'white'
  },
  providerGuideList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  providerGuide: {
    border: '1px solid #e1e7eb',
    borderRadius: '4px',
    padding: '8px'
  },
  providerName: {
    fontWeight: '800',
    fontSize: '12px',
    color: '#243b53',
    marginBottom: '5px'
  },
  providerMockWindow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px',
    fontSize: '11px',
    color: '#627d98',
    background: '#f7f8fa',
    borderRadius: '5px',
    padding: '7px'
  },
  providerArrow: {
    color: '#9aa8b3'
  },
  providerShareButton: {
    border: '1px solid #9fb5c4',
    borderRadius: '5px',
    padding: '2px 6px',
    color: '#2f6f9f',
    background: 'white',
    fontWeight: '700'
  },
  aiDisclosureReadOnly: {
    marginTop: '12px',
    border: '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '12px',
    background: '#f8fafb',
    fontSize: '13px',
    color: '#243b53',
    lineHeight: '1.5'
  },
  aiDisclosureLinkRow: {
    marginTop: '5px',
    overflowWrap: 'anywhere'
  },
  wrap: { minHeight: 0, background: 'transparent' },
  content: { padding: 0, maxWidth: 'none', margin: 0 },
  muted: { color: '#627d98', fontSize: '14px', padding: '20px' },
  heading: { margin: '0 0 8px', fontSize: '26px', color: '#243b53', fontWeight: '700' },
  summary: { fontSize: '14px', color: '#627d98', marginBottom: '8px' },
  refreshNote: { fontSize: '11px', color: '#829ab1', marginBottom: '20px' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '5px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '5px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  backLink: {
    border: 'none', background: 'none', color: '#2f6f9f',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    padding: '0 0 16px', display: 'block'
  },
  weekList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  weekRow: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', background: 'white', border: '1px solid #d9e2ec',
    borderRadius: '6px', cursor: 'pointer', textAlign: 'left'
  },
  weekLeft: { flex: 1 },
  weekNum: { fontWeight: '700', fontSize: '14px', color: '#243b53' },
  weekDate: { fontSize: '12px', color: '#627d98', marginTop: '2px' },
  weekRight: { flexShrink: 0 },
  arrow: { color: '#627d98', fontSize: '20px', flexShrink: 0 },
  submittedPill: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  draftPill: {
    background: '#fff4d7', color: '#8a5b00', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  feedbackPill: {
    background: '#eef5fa', color: '#2f6f9f', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  openPill: {
    background: '#eef5fa', color: '#2f6f9f', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  notOpenPill: {
    background: '#edf1f5', color: '#627d98', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  closedPill: {
    background: '#faeaea', color: '#a12a2a', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  form: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px', padding: '20px'
  },
  responseForm: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px',
    padding: '20px', marginBottom: '16px'
  },
  responseBox: {
    background: '#f7f8fa', border: '1px solid #d9e2ec', borderRadius: '6px',
    padding: '14px', marginBottom: '16px'
  },
  feedbackPendingBox: {
    border: '1px dashed #c8d2dc',
    borderRadius: '6px',
    background: '#f7f8fa',
    padding: '14px',
    marginTop: '16px',
    marginBottom: '16px'
  },
  feedbackBox: {
    background: '#eef5fa', border: '1px solid #c5d6e3', borderRadius: '6px',
    padding: '16px', marginBottom: '16px'
  },
  feedbackLabel: {
    fontSize: '11px', fontWeight: '700', color: '#627d98',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px'
  },
  feedbackText: { fontSize: '15px', color: '#243b53', lineHeight: '1.6' },
  completionStatus: { fontSize: '13px', color: '#2f6f9f', marginTop: '8px', fontWeight: '600' },
  lockedNote: { fontSize: '12px', color: '#627d98', marginTop: '10px', fontStyle: 'italic' },
  extensionNotice: {
    background: '#eef5fa',
    border: '1px solid #d7e4ed',
    borderRadius: '5px',
    padding: '9px 12px',
    color: '#2f6f9f',
    fontSize: '13px',
    marginBottom: '14px'
  },
  submittedBox: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px', padding: '16px'
  },
  closedBanner: {
    background: '#edf1f5', border: '1px solid #d9e2ec', borderRadius: '5px',
    padding: '12px 16px', fontSize: '14px', color: '#627d98', marginTop: '12px'
  },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#243b53' },
  textarea: {
    width: '100%', border: '1px solid #d9e2ec', borderRadius: '5px',
    padding: '10px 12px', fontSize: '15px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical', lineHeight: '1.6', marginBottom: '12px'
  },
  btnRow: { display: 'flex', gap: '10px' },
  secondaryBtn: {
    background: 'transparent', color: '#2f6f9f', border: '1px solid #c5d6e3',
    borderRadius: '5px', padding: '10px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  submitBtn: {
    flex: 1, background: '#2f6f9f', color: 'white', border: 'none',
    borderRadius: '5px', padding: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  }
};